import type {
  AuthoringSessionStatus,
  AuthoringSessionSummary,
  InsightAuthoringHistoryEntry,
} from '../models/insightAuthoring.types'

const SOURCE_STATUS: Record<InsightAuthoringHistoryEntry['source'], AuthoringSessionStatus> = {
  draft_propose: 'draft',
  draft_revise: 'draft',
  draft_save: 'saved',
  draft_dry_run: 'validated',
  draft_activate: 'activated',
}

const STATUS_PRIORITY: Record<AuthoringSessionStatus, number> = {
  draft: 0,
  saved: 1,
  validated: 2,
  activated: 3,
}

function timestamp(value: string | undefined): number {
  const parsed = value ? Date.parse(value) : Number.NaN
  return Number.isFinite(parsed) ? parsed : 0
}

/** BE-15-10 fallback: regroup the measured flat audit rows without leaking it into components. */
export function groupAuthoringHistory(
  entries: readonly InsightAuthoringHistoryEntry[],
  limit = 10,
): AuthoringSessionSummary[] {
  const sessions = new Map<string, AuthoringSessionSummary>()
  for (const entry of entries) {
    const sessionId = entry.session_id?.trim()
    if (!sessionId) continue
    const nextStatus = SOURCE_STATUS[entry.source]
    const current = sessions.get(sessionId)
    if (!current) {
      sessions.set(sessionId, {
        sessionId,
        status: nextStatus,
        configId: entry.config_id ?? null,
        turnCount: entry.source === 'draft_propose' || entry.source === 'draft_revise' ? 1 : 0,
        lastTouched: entry.created_at ?? null,
      })
      continue
    }
    if (STATUS_PRIORITY[nextStatus] > STATUS_PRIORITY[current.status]) current.status = nextStatus
    if (entry.config_id !== undefined) current.configId = entry.config_id
    if (entry.source === 'draft_propose' || entry.source === 'draft_revise') current.turnCount += 1
    if (timestamp(entry.created_at) > timestamp(current.lastTouched ?? undefined)) {
      current.lastTouched = entry.created_at ?? null
    }
  }
  return [...sessions.values()]
    .sort(
      (left, right) =>
        timestamp(right.lastTouched ?? undefined) - timestamp(left.lastTouched ?? undefined),
    )
    .slice(0, Math.max(0, limit))
}
